"use strict";
// Copyright 2019 Centrality Investments Limited
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//     http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (Object.hasOwnProperty.call(mod, k)) result[k] = mod[k];
    result["default"] = mod;
    return result;
};
Object.defineProperty(exports, "__esModule", { value: true });
const crml_generic_asset_1 = require("@cennznet/crml-generic-asset");
const util_1 = require("@cennznet/util");
const rxjs_1 = require("rxjs");
const operators_1 = require("rxjs/operators");
const derives = __importStar(require("./derives"));
class CennzxSpotRx {
    static create(api) {
        if (api.cennzxSpot) {
            return rxjs_1.of(api.cennzxSpot);
        }
        const ga = api.genericAsset ? rxjs_1.of(api.genericAsset) : crml_generic_asset_1.GenericAssetRx.create(api);
        return ga.pipe(operators_1.switchMap(ga => {
            const cennzxSpot = new CennzxSpotRx(api, ga);
            api._options.derives = { ...api._options.derives, cennzxSpot: derives };
            return rxjs_1.from(api.loadMeta()).pipe(operators_1.mapTo(cennzxSpot));
        }));
    }
    constructor(api, ga) {
        util_1.assert(api._options.derives.cennzxSpot || (api._derive || {}).cennzxSpot, "init cennzx spot's derives first");
        this._api = api;
        if (api.genericAsset) {
            this._ga = api.genericAsset;
        }
        else if (ga) {
            this._ga = ga;
        }
    }
    get api() {
        return this._api;
    }
    get ga() {
        return this._ga;
    }
    /**
     * add liquidity
     * @param {assetId} - The trade asset ID
     * @param {minLiquidity} - The minimum liquidity to add
     * @param {maxAssetAmount} - Amount of trade asset to add
     * @param {coreAmount} - Amount of core asset to add
     */
    addLiquidity(assetId, minLiquidity, maxAssetAmount, coreAmount, expire) {
        return this.api.tx.cennzxSpot.addLiquidity(assetId, minLiquidity, maxAssetAmount, coreAmount);
    }
    /**
     * remove liquidity
     * @param assetId - The asset to remove
     * @param assetAmount - Amount of exchange asset to burn
     * @param minAssetWithdraw - The minimum trade asset withdrawn
     * @param minCoreWithdraw - The minimum core asset withdrawn
     */
    removeLiquidity(assetId, assetAmount, minAssetWithdraw, minCoreAssetWithdraw) {
        return this.api.tx.cennzxSpot.removeLiquidity(assetId, assetAmount, minAssetWithdraw, minCoreAssetWithdraw);
    }
    /**
     * Asset 1 to asset 2 swap output
     * @param assetSold The asset to sell
     * @param assetBuy The asset to Buy
     * @param amountBought amount of asset 2 to buy
     * @param maxAmountSold maximum amount of asset 1 allowed to sell
     */
    assetSwapOutput(assetSold, assetBought, amountBought, maxAmountSold) {
        return this.api.tx.cennzxSpot.assetSwapOutput(null, assetSold, assetBought, amountBought, maxAmountSold);
    }
    /**
     * Asset 1 to asset 2 transfer output
     * @param recipient - The address that receives the output asset
     * @param assetSold The asset to sell
     * @param assetBuy The asset to buy
     * @param amountBought amount of asset 2 to buy
     * @param maxAmountSold maximum amount of asset allowed to sell
     */
    assetTransferOutput(recipient, assetSold, assetBought, amountBought, maxAmountSold) {
        return this.api.tx.cennzxSpot.assetSwapOutput(recipient, assetSold, assetBought, amountBought, maxAmountSold);
    }
    /**
     * Asset 1 to asset 2 swap input
     * @param assetSold The asset to sell
     * @param assetBuy The asset to buy
     * @param amountSell amount of trade asset 1 to sell
     * @param minSale Min trade asset 2 to receive from sale (output)
     */
    assetSwapInput(assetSold, assetBought, amountSell, minReceive) {
        return this.api.tx.cennzxSpot.assetSwapInput(null, assetSold, assetBought, amountSell, minReceive);
    }
    /**
     * Asset 1 to asset 2 transfer input
     * @param recipient - The address that receives the output asset
     * @param assetSold The asset to sell
     * @param assetBuy The asset to buy
     * @param amountSell amount of trade asset to sell
     * @param minSale Min core asset to receive from sale (output)
     */
    assetTransferInput(recipient, assetSold, assetBought, amountSell, minReceive) {
        return this.api.tx.cennzxSpot.assetSwapInput(recipient, assetSold, assetBought, amountSell, minReceive);
    }
    /**
     * Query the total liquidity of an exchange pool
     * @param assetId
     */
    get getTotalLiquidity() {
        const _fn = this.api.derive.cennzxSpot.totalLiquidity;
        _fn.at = this.api.derive.cennzxSpot.totalLiquidityAt;
        return _fn;
    }
    /**
     * Query the core asset id
     */
    get getCoreAssetId() {
        return this.api.query.cennzxSpot.coreAssetId;
    }
    // tslint:disable:member-ordering
    /**
     * Query liquidity balance for an account
     * @param {(AnyNumber,AnyNumber)} coreAssetIs, assetId The id of the asset
     * @param {AnyAddress} address The address of the account
     */
    get getLiquidityBalance() {
        const _fn = this.api.derive.cennzxSpot.liquidityBalance;
        _fn.at = this.api.derive.cennzxSpot.liquidityBalanceAt;
        return _fn;
    }
    /**
     * query the price to buy amountBought asset
     * @param assetSold assetId to sell
     * @param assetBought assetId to buy
     * @param amountBought amount of assetBought to buy
     */
    get getOutputPrice() {
        const _fn = this.api.derive.cennzxSpot.outputPrice;
        _fn.at = this.api.derive.cennzxSpot.outputPriceAt;
        return _fn;
    }
    /**
     * query the price to sell asset of #amount
     * @param assetSold assetId to sell
     * @param assetBought assetId to buy
     * @param amountSold amount of assetSold to sell
     */
    get getInputPrice() {
        const _fn = this.api.derive.cennzxSpot.inputPrice;
        _fn.at = this.api.derive.cennzxSpot.inputPriceAt;
        return _fn;
    }
}
exports.CennzxSpotRx = CennzxSpotRx;
//# sourceMappingURL=CennzxSpotRx.js.map